import { MaybeRef } from '@vueuse/core'
import { isNative } from '../../utils'
import { computed, unref } from 'vue'
import { DataRow, ColumnsMeta, ColumnMeta } from '../../types/components/data-rendering'

type UseTransformStore = {
  /**take array of keys from input table, and return meta/cols of only those keys */
  subset: (
    subsetKeys: string[],
    options?: { renameKeys?: { from: string; to: ColumnMeta }[] }
  ) => {
    meta: { columns: ColumnsMeta; [key: string]: any }
    data: DataRow[]
  }
  filter: (filterKeys: string[]) => {
    meta: { columns: ColumnsMeta; [key: string]: any }
    data: DataRow[]
  }
  subsetData: (
    subsetKeys: string[],
    options?: { renameKeys?: { from: string; to: ColumnMeta }[] }
  ) => DataRow[]
  subsetMetaByKeys: (
    subsetKeys: string[],
    options?: { renameKeys?: { from: string; to: ColumnMeta }[] }
  ) => { columns: ColumnsMeta; [key: string]: any }
}
export const useTransformStore = <
  T extends { meta: { columns: ColumnsMeta; [key: string]: any }; data: DataRow[] }
>(
  storeData: MaybeRef<T>
): UseTransformStore => {
  const subset = (
    subsetKeys: string[],
    options?: { renameKeys?: { from: string; to: ColumnMeta }[] }
  ) => {
    const { columns, ...rest } = unref(storeData).meta
    const data = unref(storeData).data

    const subsetCols = subsetTableData(columns, subsetKeys) as ColumnsMeta
    //reorder cols
    const newColumns: any = {}
    subsetKeys.forEach((key) => {
      if (subsetCols[key]) {
        newColumns[key] = subsetCols[key]
      }
    })
    if (typeof options !== 'undefined' && typeof options.renameKeys !== 'undefined') {
      options.renameKeys.forEach((key) => {
        newColumns[key.from] = key.to
      })
    }
    return {
      meta: {
        columns: newColumns,
        ...rest,
      },
      data: data.map((row) => {
        return subsetTableData(row, subsetKeys)
      }),
    }
  }

  const filter = (filterKeys: string[]) => {
    const { columns, ...rest } = unref(storeData).meta
    const data = unref(storeData).data
    return {
      meta: {
        columns: filterTableData(columns, filterKeys),
        ...rest,
      },
      data: data.map((row) => {
        return filterTableData(row, filterKeys)
      }),
    }
  }

  const subsetData = (filterKeys: string[]) => {
    const data = unref(storeData).data
    return data.map((row) => {
      return subsetTableData(row, filterKeys)
    })
  }

  const subsetMetaByKeys = (
    subsetKeys: string[],
    options?: { renameKeys?: { from: string; to: ColumnMeta }[] }
  ) => {
    const { columns, ...rest } = unref(storeData).meta

    //reorder cols
    const newColumns: any = {}
    subsetKeys.forEach((key) => {
      if (columns[key]) {
        newColumns[key] = columns[key]
      }
    })

    if (typeof options !== 'undefined' && typeof options.renameKeys !== 'undefined') {
      options.renameKeys.forEach((key) => {
        newColumns[key.from] = key.to
      })
    }

    return {
      columns: newColumns,
      ...rest,
    }
  }

  return {
    subset,
    filter,
    subsetData,
    subsetMetaByKeys,
  }
}
/*
 * Helpers
 */

const pipeTransformTable =
  (fn: (...args: any[]) => boolean) => (objectKeys: { [key: string]: any }, keys: string[]) => {
    return Object.keys(objectKeys).reduceRight((acc, curr) => {
      if (fn(curr, keys)) {
        return { [curr]: objectKeys[curr], ...acc }
      } else {
        return acc
      }
    }, {})
  }

const subsetTable = (objectKey: string, keys: string[]): boolean => keys.includes(objectKey)
const filterTable = (objectKey: string, keys: string[]): boolean => !keys.includes(objectKey)

const subsetTableData = pipeTransformTable(subsetTable)
const filterTableData = pipeTransformTable(filterTable)

export const onDeviceColumn = computed((): undefined | { [key: string]: ColumnMeta } => {
  if (isNative.value) {
    return {
      on_device: {
        label: {
          en_US: 'Downloaded',
          es_MX: 'Downloaded',
          fr_CA: 'Téléchargé',
        },
        type: 'boolean',
      },
    }
  } else {
    return undefined
  }
})

export const subsetMetaByKeys = (
  originalMeta: { columns: ColumnsMeta; [key: string]: any },
  subsetKeys: string[],
  options?: { renameKeys?: { from: string; to: ColumnMeta }[] }
) => {
  const { columns, ...rest } = originalMeta

  //reorder cols
  const newColumns: any = {}
  subsetKeys.forEach((key) => {
    if (columns[key]) {
      newColumns[key] = columns[key]
    }
  })

  if (typeof options !== 'undefined' && typeof options.renameKeys !== 'undefined') {
    options.renameKeys.forEach((key) => {
      newColumns[key.from] = key.to
    })
  }

  return {
    columns: newColumns,
    ...rest,
  }
}
